import React, { Fragment } from 'react';
import classnames from 'classnames';
import chunk from 'lodash/chunk';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useQuery } from '@apollo/client';

import { Picture } from 'formlabs-web-components/Media';
import { GridContainer, Grid, Cell } from 'formlabs-web-components/Grid';
import { Link } from 'formlabs-web-components/Link';
import useIsMobile from '../../hooks/useIsMobile';
import SocialMediaIcons from '../content/SocialMediaIcons';
import FooterMenu from './FooterMenu';
import FooterMenuMobile from './FooterMenuMobile';
import NewsletterSignUp from '../forms/NewsletterSignUp';
import RegionFlag from './RegionFlag';
import logoBlack from '../../../../public/logo_black.svg';
import logoWhite from '../../../../public/logo_white.svg';

import { CountrySelectionModal } from '../../../sites/formlabs/components/countrySelection/CountrySelection';

import query from './menu.graphql';
import './Footer.module.css';

const pickLogo = color => {
  const logos = {
    black: logoBlack,
    white: logoWhite
  };

  if (logos[color]) return logos[color];
  return logos['black'];
};
const Footer = ({ countrySite, countries, white, activeRegion }) => {
  const { path: countryPath, language, regionFlag } = countrySite;
  const { t } = useTranslation();
  const mobile = useIsMobile(1023);

  const { data } = useQuery(query, {
    variables: { draft: false, path: countryPath, kind: 'FOOTER' }
  });
  const menu = get(data, 'menu', null);

  if (!menu) {
    return null;
  }

  const splitMenu = chunk(menu, 2);

  return (
    <nav
      styleName={classnames('footer', { white: white })}
      aria-label={t('Page footer navigation')}
    >
      <GridContainer>
        <Grid direction="x" primaryAlignment="align-center" gutters="margin">
          <Cell largeSize={4}>
            <div styleName="footer-logo">
              <Picture
                largeImage={pickLogo(white ? 'black' : 'white')}
                lazyload={false}
                width={145}
                alt={t('Formlabs logo')}
              />
            </div>

            <div styleName="region">
              <CountrySelectionModal countries={countries}>
                <div styleName="region-select">
                  <RegionFlag region={activeRegion} fallback={regionFlag} />{' '}
                  <span styleName="region-text">{t('Change Region')}</span>
                </div>
              </CountrySelectionModal>
            </div>
          </Cell>
          {!mobile && <FooterMenu splitMenu={splitMenu} />}
          {mobile && <FooterMenuMobile data={menu} />}

          <Cell largeSize={7} largeOffset={1}>
            <p styleName="email-area">
              {t('Enter your email address for updates')}
            </p>
            <NewsletterSignUp layoutType="default" />

            <SocialMediaIcons
              color={white ? 'black' : 'white'}
              iconSize="1.33rem"
              spaceBetween="1.1rem"
              align="left"
            />
          </Cell>
        </Grid>
      </GridContainer>
      <div styleName="bottom-most-footer">
        <GridContainer>
          <Grid direction="x" primaryAlignment="align-justify">
            <Cell largeSize={4} largeOrder="1" mediumOrder="2" smallOrder="2">
              © Formlabs {new Date().getFullYear()}
            </Cell>
            <Cell largeSize={8} largeOrder="2">
              <div styleName="bottom-links">
                <Link to={`${countryPath}privacy-policy/`}>
                  {t('Privacy Policy')}
                </Link>
                {' · '}
                <Link to={`${countryPath}terms-of-service/`}>
                  {t('Terms of Service')}
                </Link>
                {countrySite.language === 'en' && (
                  <Fragment>
                    {' · '}
                    <Link to={`${countryPath}contests-and-sweepstakes/`}>
                      {t('Contests and Sweepstakes')}
                    </Link>
                  </Fragment>
                )}
                {' · '}
                <Link
                  to={`https://support.formlabs.com/s/topic/0TO1Y000000Iw01WAC/faq?language=${language}`}
                >
                  {t('FAQ')}
                </Link>
              </div>
            </Cell>
          </Grid>
        </GridContainer>
      </div>
    </nav>
  );
};

export default connect(state => ({
  activeRegion: state.i18n.region
}))(Footer);
